import React, { useCallback, useEffect, useState } from 'react';
import {
  chantiersApi,
  CHANTIER_STATUSES,
  CHANTIER_CATEGORIES,
  STATUS_LABEL,
  CATEGORY_LABEL,
} from '../../services/chantiersApi';
import { getToken } from '../auth';
import '../styles/Chantiers.css';
import './admin-form.css';

const EMPTY_FORM = {
  title: '', description: '',
  status: 'planifie', category: 'voirie',
  pct: 0, budget: '', delivery: '',
  imageSeed: '', imageUrl: '',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusPillClass(status) {
  switch (status) {
    case 'en_cours':     return 'live';
    case 'phase_finale': return 'warn';
    case 'planifie':     return 'ocean';
    case 'suspendu':     return 'muted';
    case 'livre':        return 'live';
    default:             return 'muted';
  }
}

const Chantiers = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus]   = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await chantiersApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        status:   filterStatus   !== 'all' ? filterStatus   : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, filterCategory, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { chantier } = await chantiersApi.adminGet(token, id);
      setForm({
        title:       chantier.title,
        description: chantier.description || '',
        status:      chantier.status,
        category:    chantier.category,
        pct:         chantier.pct,
        budget:      chantier.budget || '',
        delivery:    chantier.delivery || '',
        imageSeed:   chantier.imageSeed || '',
        imageUrl:    chantier.imageUrl || '',
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger le chantier : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        category: form.category,
        pct: Number(form.pct),
        budget: form.budget,
        delivery: form.delivery,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
      };
      if (editingId) await chantiersApi.adminUpdate(token, editingId, payload);
      else           await chantiersApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement le chantier « ${title} » ?`)) return;
    try {
      await chantiersApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onQuickStatus = async (id, status) => {
    try {
      await chantiersApi.adminUpdate(token, id, { status });
      await load();
    } catch (err) {
      alert('Action impossible : ' + err.message);
    }
  };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Chantiers</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un chantier…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouveau chantier
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              CMS · {total} chantier{total > 1 ? 's' : ''}
            </span>
            <h1>Les chantiers,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Suivez l'avancement, mettez à jour les pourcentages, publiez ou archivez. Tout est synchronisé avec /mairie.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {CHANTIER_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {CHANTIER_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Chantier</th>
                <th>Catégorie</th>
                <th>Statut</th>
                <th>Progression</th>
                <th>Livraison</th>
                <th>Budget</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun chantier. Cliquez « Nouveau chantier » pour démarrer.
                </td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} onClick={() => openEdit(c.id)}>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</b>
                    {c.description && (
                      <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.description}
                      </small>
                    )}
                  </td>
                  <td>
                    <span className="cat-pill">{CATEGORY_LABEL[c.category] || c.category}</span>
                  </td>
                  <td>
                    <span className={`status-pill ${statusPillClass(c.status)}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                  </td>
                  <td style={{ minWidth: 140 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <b style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 400, color: 'var(--char-900)' }}>{c.pct}%</b>
                      <div style={{ flex: 1, height: 4, background: 'var(--bone-2)', borderRadius: 999, overflow: 'hidden', maxWidth: 80 }}>
                        <div
                          style={{
                            width: `${c.pct}%`, height: '100%',
                            background: c.status === 'livre'
                              ? '#34A853'
                              : c.status === 'planifie'
                                ? 'var(--ocean-300)'
                                : 'linear-gradient(90deg, var(--sunset), var(--gold))',
                            borderRadius: 999,
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)', letterSpacing: '.06em' }}>
                    {c.delivery || '—'}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>
                    {c.budget || '—'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      {c.status === 'en_cours' && (
                        <button title="Suspendre" onClick={() => onQuickStatus(c.id, 'suspendu')}>⏸</button>
                      )}
                      {c.status === 'suspendu' && (
                        <button title="Reprendre" onClick={() => onQuickStatus(c.id, 'en_cours')}>▶</button>
                      )}
                      <button title="Éditer" onClick={() => openEdit(c.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(c.id, c.title)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} chantier{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE FORMULAIRE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Éditer le chantier' : 'Nouveau chantier'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title || '—' : 'Ouvrir un chantier'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form onSubmit={onSubmit} className="admin-form">
              <Field label="Intitulé du chantier">
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Réhabilitation de la rue 8"
                />
              </Field>

              <Field label="Description courte">
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Ce qui sera fait, par qui, dans quel délai"
                />
              </Field>

              <div className="admin-form-row">
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {CHANTIER_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </Field>
                <Field label="Statut">
                  <select
                    required
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    {CHANTIER_STATUSES.filter((s) => s.id !== 'all').map((s) =>
                      <option key={s.id} value={s.id}>{s.lbl}</option>
                    )}
                  </select>
                </Field>
              </div>

              <div>
                <span className="admin-field-label" style={{ display: 'block', marginBottom: 8 }}>
                  Progression — {form.pct}%
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={form.pct}
                  onChange={(e) => setForm((f) => ({ ...f, pct: e.target.value }))}
                  style={{ width: '100%', accentColor: 'var(--sunset)' }}
                />
                <small style={{ display: 'block', marginTop: 6, fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.14em', color: 'var(--char-500)' }}>
                  Astuce — passer à 80%+ force le statut « Phase finale », 100% passe en « Livré » (sauf statut explicite).
                </small>
              </div>

              <div className="admin-form-row">
                <Field label="Budget (libre)">
                  <input
                    type="text"
                    value={form.budget}
                    onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                    placeholder="180 M FCFA"
                  />
                </Field>
                <Field label="Délai de livraison (libre)">
                  <input
                    type="text"
                    value={form.delivery}
                    onChange={(e) => setForm((f) => ({ ...f, delivery: e.target.value }))}
                    placeholder="Juillet 2026"
                  />
                </Field>
              </div>

              <Field label="Image · seed Picsum ou URL">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-chantier-rue8  ·  ou  https://…"
                />
              </Field>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Chantiers;
